import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';
import { AlertTriangle, RefreshCcw, ArrowLeft } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Button } from '@/components/ui/Button';

/**
 * Pretty error UI for both:
 *   • Route-level errors (loader throws, component throws inside a route)
 *   • Top-level fallback when the global ErrorBoundary catches a render error
 *
 * Pass `error` explicitly when used as the global fallback;
 * leave it undefined when used as a React Router `errorElement` —
 * `useRouteError()` will pick up the thrown value.
 */
interface ErrorPageProps {
  error?: unknown;
  reset?: () => void;
}

export function ErrorPage({ error: errorProp, reset }: ErrorPageProps) {
  // useRouteError throws if used outside a router context — handle gracefully
  let routerError: unknown;
  try {
    routerError = useRouteError();
  } catch {
    routerError = undefined;
  }
  const error = errorProp ?? routerError;

  const { title, status, message } = describe(error);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <Logo size="lg" />

      <div className="mt-12 flex h-16 w-16 items-center justify-center rounded-2xl bg-warning/10 text-warning">
        <AlertTriangle className="h-8 w-8" />
      </div>
      {status && (
        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-warning">
          Error {status}
        </p>
      )}
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink">
        {title}
      </h1>
      <p className="mt-4 max-w-lg text-ink-muted">{message}</p>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {reset && (
          <Button onClick={reset} iconLeft={<RefreshCcw className="h-4 w-4" />}>
            Try again
          </Button>
        )}
        <ButtonLink to="/" variant={reset ? 'outline' : 'primary'} iconLeft={<ArrowLeft className="h-4 w-4" />}>
          Back to home
        </ButtonLink>
      </div>

      {import.meta.env.DEV && error instanceof Error && (
        <details className="mt-10 max-w-2xl w-full text-left rounded-xl border border-border bg-surface p-4">
          <summary className="text-xs font-semibold uppercase tracking-wider text-ink-subtle cursor-pointer">
            Stack trace (dev only)
          </summary>
          <pre className="mt-3 whitespace-pre-wrap break-words text-xs text-ink-muted font-mono">
            {error.stack ?? error.message}
          </pre>
        </details>
      )}

      <p className="mt-10 text-xs text-ink-subtle">
        Persisting? <Link to="/contact" className="link">Let us know</Link>.
      </p>
    </div>
  );
}

/** Normalise any thrown thing into a friendly title + status + message. */
function describe(error: unknown): { title: string; status?: number; message: string } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return { status: 404, title: 'Page not found', message: "The page you're looking for doesn't exist or has moved." };
    }
    if (error.status === 401) {
      return { status: 401, title: 'You need to sign in', message: 'Sign in to access this page.' };
    }
    if (error.status === 403) {
      return { status: 403, title: 'Access denied', message: "You don't have permission to view this page." };
    }
    return {
      status: error.status,
      title: error.statusText || 'Something went wrong',
      message: typeof error.data === 'string' ? error.data : 'Please try again in a moment.',
    };
  }
  if (error instanceof Error) {
    return {
      title: 'Something went wrong',
      message: 'We hit an unexpected error rendering this page. The team has been notified.',
    };
  }
  return {
    title: 'Something went wrong',
    message: 'Please try again in a moment.',
  };
}
